import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Flag, Users, TrendingUp, Target, Lightbulb, Award, CheckCircle2, Rocket } from 'lucide-react';

const EUProjectsPage = () => {
  const expertise = [
    {
      id: 'horizon-europe',
      image: 'https://images.unsplash.com/photo-1758206523826-a65d4cf070aa?crop=entropy&cs=srgb&fm=jpg&q=85',
      title: 'Horizon Europe',
      description: 'Research and innovation projects aligned with EU priorities and Green Deal objectives',
      link: 'https://research-and-innovation.ec.europa.eu/funding/funding-opportunities/funding-programmes-and-open-calls/horizon-europe_en'
    },
    {
      id: 'interreg',
      image: 'https://images.unsplash.com/photo-1593080358201-08e4ff5f93d9?crop=entropy&cs=srgb&fm=jpg&q=85',
      title: 'Interreg',
      description: 'Cross-border and transnational cooperation for sustainable regional development',
      link: 'https://interreg.eu/'
    },
    {
      id: 'erasmus-plus',
      image: 'https://images.unsplash.com/photo-1770235622334-7b721261a230?crop=entropy&cs=srgb&fm=jpg&q=85',
      title: 'Erasmus+',
      description: 'With a budget of some €26 billion, Erasmus+ 2021-2027 offers more mobility and cooperation opportunities than ever before',
      link: 'https://erasmus-plus.ec.europa.eu/'
    },
    {
      id: 'life-programme',
      image: 'https://images.unsplash.com/photo-1593069567131-53a0614dde1d?crop=entropy&cs=srgb&fm=jpg&q=85',
      title: 'LIFE Programme',
      description: 'Environment and climate action projects for biodiversity and circular economy',
      link: 'https://cinea.ec.europa.eu/programmes/life_en'
    },
  ];

  const capabilities = [
    {
      id: 'consortium-building',
      icon: <Users className="w-6 h-6" />,
      title: 'Consortium Building',
      description: 'Strategic partner identification and coalition formation across European networks',
    },
    {
      id: 'work-package',
      icon: <TrendingUp className="w-6 h-6" />,
      title: 'Work Package Leadership',
      description: 'Technical coordination and deliverable management for complex EU projects',
    },
    {
      id: 'impact-assessment',
      icon: <Award className="w-6 h-6" />,
      title: 'Impact Assessment',
      description: 'Evidence-based evaluation frameworks and measurable impact indicators',
    },
    {
      id: 'replication-scaling',
      icon: <Target className="w-6 h-6" />,
      title: 'Replication & Scaling',
      description: 'Transferability strategies and scalability analysis for project results',
    },
  ];

  const policyAlignment = [
    'EU Green Deal',
    'Cohesion Policy 2021-2027',
    'Fit for 55 Package',
    'Biodiversity Strategy 2030',
    'Circular Economy Action Plan',
    'Farm to Fork Strategy',
    'Smart Cities Mission',
    'Digital Europe Programme',
  ];

  const projectTypes = [
    {
      id: 'innovation',
      type: 'Innovation Projects',
      description: 'Pilot demonstrations and technological innovation for sustainability',
      examples: ['Climate adaptation solutions', 'Smart destination platforms', 'Circular economy models'],
    },
    {
      id: 'policy',
      type: 'Policy Projects',
      description: 'Policy development and multi-level governance frameworks',
      examples: ['Regulatory framework design', 'Stakeholder consultation', 'Governance model testing'],
    },
    {
      id: 'capacity',
      type: 'Capacity Building',
      description: 'Knowledge transfer and institutional strengthening initiatives',
      examples: ['Professional training programs', 'Knowledge platforms', 'Best practice exchange'],
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/14332639/pexels-photo-14332639.jpeg"
            alt="Wind Energy"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Projects & Expertise</h1>
            <p className="text-2xl text-gray-200 leading-relaxed mb-8">
              Strategic leadership in European innovation and policy projects—from Horizon Europe to Interreg and beyond
            </p>
            <p className="text-lg text-gray-300">
              Consortium building · Work package coordination · Impact assessment · Scalability strategies
            </p>
          </div>
        </div>
      </section>

      {/* Programme Expertise */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">EU Programmes Expertise</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Deep knowledge of EU funding mechanisms and project requirements
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item) => (
              <Card key={item.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <a 
                    href={item.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xl font-bold text-red-600 hover:text-red-700 mb-3 inline-block hover:underline"
                  >
                    {item.title} ↗
                  </a>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Core Capabilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              End-to-end project support from design to delivery
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((item, index) => (
              <div key={index} className="flex items-start space-x-6 p-8 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Policy Alignment */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">EU Policy Alignment</h2>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Our projects are strategically aligned with major EU policy frameworks and initiatives, ensuring relevance, compliance, and maximum impact.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {policyAlignment.map((policy) => (
                  <div key={policy} className="flex items-center space-x-3 p-3 rounded-lg bg-gradient-to-r from-gray-50 to-red-50 border border-red-100">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium text-sm">{policy}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1716810743177-27a73804e820"
                alt="Renewable Energy"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-red-600 text-white p-6 rounded-xl shadow-2xl">
                <Award className="w-10 h-10 mb-2" />
                <p className="text-sm font-semibold">Policy-Driven Innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Project Types</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Diverse project categories tailored to different objectives and challenges
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {projectTypes.map((project) => (
              <Card key={project.id} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-red-400 mb-4">{project.type}</h3>
                  <p className="text-gray-200 mb-6 leading-relaxed">{project.description}</p>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Examples:</p>
                    <ul className="space-y-2">
                      {project.examples.map((example) => (
                        <li key={example} className="flex items-start space-x-2 text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Why Work With Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven expertise in delivering successful EU-funded projects
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              'Extensive network of European partners',
              'Track record in Horizon 2020 and Horizon Europe',
              'Technical and scientific excellence',
              'Strong policy and regulatory knowledge',
              'Multi-stakeholder coordination expertise',
              'Innovation and pilot project experience',
            ].map((item) => (
              <div key={item} className="flex items-start space-x-4 p-6 rounded-xl bg-gradient-to-r from-gray-50 to-red-50 border-2 border-red-100 hover:border-red-500 transition-all duration-300">
                <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-lg text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Join Our Next EU Project</h2>
          <p className="text-xl mb-10 text-gray-100">
            Looking for expert partners for your EU-funded initiative? Let's collaborate
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6">
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EUProjectsPage;
